import { useMutation } from "@apollo/client";
import { UPDATE_TRANSACTION_SETTING_POLICIES } from "../graphql/company";
import { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  CategorySetting,
  PendingCategorySetting,
  TransactionPolicy,
} from "./settingPolicy.types";

interface UseTransactionPoliciesSettingParams {
  refetchSettingPolicies: () => Promise<unknown>;
}

/** Holds the unsaved category edits for one transaction policy. */
export const useTransactionPoliciesSetting = ({
  refetchSettingPolicies,
}: UseTransactionPoliciesSettingParams) => {
  const { t } = useTranslation();

  const [updateTransactionSettingPolicies] = useMutation(
    UPDATE_TRANSACTION_SETTING_POLICIES,
  );

  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedPolicySetting, setSelectedPolicySetting] =
    useState<TransactionPolicy | null>(null);
  const [updatingSettings, setUpdatingSettings] = useState<
    PendingCategorySetting[]
  >([]);

  const withoutCategory = (categoryId: string) =>
    updatingSettings.filter((item) => item.categoryId !== categoryId);

  const addSetting = (setting: PendingCategorySetting) => {
    setUpdatingSettings([...withoutCategory(setting.categoryId), setting]);
  };

  const removeSetting = (setting: CategorySetting) => {
    setUpdatingSettings(withoutCategory(setting.categoryId));
  };

  /** Resolves true only when the mutation and the refetch both went through. */
  const submitUpdatedSettings = async (
    settings: PendingCategorySetting[] | null = null,
  ): Promise<boolean> => {
    if (!selectedPolicySetting) return false;
    setIsUpdating(true);
    try {
      const tempSettings = settings || updatingSettings;
      const updatedCategories = tempSettings.map((item) => ({
        categoryId: item.categoryId,
        amount: Number(item.amount),
        isCategoryEnabled: item.isCategoryEnabled,
      }));
      await updateTransactionSettingPolicies({
        variables: {
          updatedCategories,
          id: selectedPolicySetting.id,
          isPolicyTypeEnabled: selectedPolicySetting.isPolicyTypeEnabled,
        },
      });
      toast.success(t("Transaction policy settings updated successfully"));
      await refetchSettingPolicies();
      setUpdatingSettings([]);
      return true;
    } catch (error) {
      toast.error(
        t("Failed to update transaction policy settings") +
          ": " +
          (error instanceof Error ? error.message : String(error)),
      );
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  const discardUpdatedSettings = () => setUpdatingSettings([]);

  const handleUpdateAllCategories = (
    settings: CategorySetting[],
    nextStatus: boolean,
  ) => {
    // Flip whatever is already pending, then add the rest at the new status.
    const tempSettings: PendingCategorySetting[] = updatingSettings.map(
      (item) => ({ ...item, isCategoryEnabled: nextStatus }),
    );
    const alreadyPending = tempSettings.map((item) => item.categoryId);
    for (const setting of settings) {
      if (!alreadyPending.includes(setting.categoryId)) {
        tempSettings.push({ ...setting, isCategoryEnabled: nextStatus });
      }
    }
    setUpdatingSettings(tempSettings);
  };

  const handleUpdateCategoriesAmount = (
    settings: CategorySetting[],
    nextAmount: string,
  ) => {
    submitUpdatedSettings(
      settings.map((item) => ({
        ...item,
        isCategoryEnabled: true,
        amount: nextAmount,
      })),
    );
  };

  return {
    isUpdating,
    addSetting,
    removeSetting,
    discardUpdatedSettings,
    submitUpdatedSettings,
    updatingSettings,
    handleUpdateAllCategories,
    setSelectedPolicySetting,
    handleUpdateCategoriesAmount,
  };
};
